<template>
  <div
    id="page-wrapper"
    class="wrap-container wrap-container--has-margin-bottom"
    role="main"
  >
    <on-page-breadcrumbs :data="breadcrumbs" />
    <div class="info-page">
      <div class="row-center-noGutter fade-in">
        <preamble-block
          class="col-12_md-8_lg-6"
          :prefix="prefix"
          :title="title"
          :preamble="preamble"
          :align="!image ? 'left': 'center'"
        />
      </div>
      <BylineShareComponent
        :author="author"
        :authordate="authorDate"
        :title="seoTitle || title"
        :description="seoDescription"
        :size="!image ? 'narrow' : 'wide'"
      />
      <div class="row-center-noGutter fade-in">
        <div
          v-if="image || videoUrl"
          class="col-12"
        >
          <image-video-component
            :alt="alt"
            :video-url="videoUrl"
            :image="image"
            :use-picture="false"
            :auto-aspect-ratio="false"
          />
        </div>
        <div class="col-12_md-10_lg-8">
          <wysiwyg
            v-if="content || isEditable"
            v-edit="'Content'"
            class="wysiwyg--contentarea"
            :content="content"
          />

          <div
            v-if="contentBlocks.length || (isEditable && contentBlocks.length)"
            v-edit="'ContentBlocks'"
          >
            <component
              :is="block.component == 'RecipeBlock' ? 'content-recipe-block' : block.component | kebab"
              v-for="block in contentBlocks"
              :key="block.contentId"
              v-bind="block"
            />
          </div>
          <!-- OneTrust Cookies List start -->
          <div
            v-if="showOneTrust"
            :class="showOneTrustClass && 'wysiwyg--contentarea'"
          >
            <div
              id="ot-sdk-cookie-policy"
              :class="showOneTrustClass && 'wysiwyg'"
            />
          </div>
          <!-- OneTrust Cookies List end -->
          <div
            v-if="content2.length"
          >
            <wysiwyg
              v-if="content2 || isEditable"
              v-edit="'Content2'"
              class="wysiwyg--contentarea"
              :content="content2"
            />
          </div>

          <div
            v-if="contentBlocks2.length || (isEditable && contentBlocks2.length)"
            v-edit="'ContentBlocks2'"
          >
            <component
              :is="block.component == 'RecipeBlock' ? 'content-recipe-block' : block.component | kebab"
              v-for="block in contentBlocks2"
              :key="block.contentId"
              v-bind="block"
            />
          </div>
        </div>

        <div
          v-if="forms.length"
          class="col-12_md-10_lg-8"
        >
          <div
            class="info-page__form-wrapper"
          >
            <Form
              :data="forms"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="accordionBlocks.length || (isEditable && accordionBlocks.length)"
      class="accordion-block-wrapper info-page-accordion"
    >
      <sub-heading
        v-if="accordionsHeading || isEditable"
        :v-edit-heading="'AccordionsHeading'"
        :heading="accordionsHeading"
      />
      <in-view
        :threshold="0"
        :threshold-type="'px'"
        class="slide-into-view-transition"
      >
        <div
          v-edit="'AccordionBlocks'"
        >
          <accordion-group
            :items="accordionBlocks | stringify('content')"
            :limit="isEditable ? 0 : accordionShowMoreLimit"
            :tracking-scope="accordionsHeading"
          />
        </div>
      </in-view>
    </div>

    <section class="content-area-wrapper">
      <sub-heading
        v-if="contentAreaTitle || isEditable"
        :v-edit-heading="'contentAreaTitle'"
        :heading="contentAreaTitle"
      />
      <block-grid
        v-if="contentArea.length || (isEditable && contentArea.length)"
        v-edit="'ContentArea'"
        :blocks="contentArea"
      />
    </section>

    <navigation-blocks
      v-if="allNavigatablePages.length"
      :navigation-blocks="allNavigatablePages"
      :display-image-state="displayImagesInNavigationBlocks"
    >
      <sub-heading
        v-if="navigationHeading || isEditable"
        :v-edit-heading="'NavigationHeading'"
        :heading="navigationHeading"
      />
    </navigation-blocks>

    <additional-navigation-blocks
      v-if="isEditable"
      :items="additionalNavigatablePages"
    />
  </div>
</template>
<script>
import Form from '@/vue/components/common/Form'
import PreambleBlock from '@/vue/blocks/PreambleBlock/preambleBlock'
import pageWithNavigation from '@/vue/mixins/pageWithNavigation'
import BylineShareComponent from '@/vue/components/bylineShareComponent/bylineShareComponent'
import ContentRecipeBlock from '@/vue/blocks/ContentRecipeBlock/contentRecipeBlock'
import IcaYoutubeVideo from '~/@ica/ica-molecules-vue/dist/YoutubeVideo'

export default {
  name: "InfoPage",
  components: {
    Form,
    PreambleBlock,
    BylineShareComponent,
    ContentRecipeBlock,
    IcaYoutubeVideo
  },
  mixins: [pageWithNavigation],
  props: {
    prefix: {
      type: String,
      default: ""
    },
    title: {
      type: String,
      default: ""
    },
    seoDescription: {
      type: String,
      default: ''
    },
    seoTitle: {
      type: String,
      default: ''
    },
    preamble: {
      type: String,
      default: ""
    },
    content: {
      type: Array,
      default: () => []
    },
    content2: {
      type: Array,
      default: () => []
    },
    contentBlocks: {
      type: Array,
      default: () => []
    },
    contentBlocks2: {
      type: Array,
      default: () => []
    },
    breadcrumbs: {
      type: Array,
      default () {
        return []
      }
    },
    forms: {
      type: Array,
      default () {
        return []
      }
    },
    author: {
      type: String,
      default: ""
    },
    authorDate: {
      type: String,
      default: ""
    },
    displayImagesInNavigationBlocks: {
      type: Boolean,
      default: false
    },
    image: {
      type: Object,
      default: null
    },
    videoUrl: {
      type: String,
      default: null
    },
    alt: {
      type: String,
      default: 'Film från youtube, saknar beskrivning'
    },
    accordionsHeading: {
      type: String,
      default: ""
    },
    accordionBlocks: {
      type: Array,
      default () {
        return []
      }
    },
    factBlocks: {
      type: Array,
      default () {
        return []
      }
    },
    accordionShowMoreLimit: {
      type: Number,
      default: 0
    },
    contentAreaTitle: {
      type: String,
      default: ""
    },
    contentArea: {
      type: Array,
      default: () => []
    },
  },
  data () {
    return {
      tooltip: false,
      show: true,
      showOneTrust: true,
      showOneTrustClass: false

    }
  },
    computed: {
      aspectRatioMode () {
        return this.$root.currentSizeLte('sm')
    },
  },
  mounted: function (){
    let currentUrl = window.location.pathname
    const showOnPages = ['/dataskydd/cookies/','/policies/cookies/', '/teststruktur/policies/cookies/', '/teststruktur/under-uppbyggnad/new-cookie-page/']
    if(!showOnPages.includes(currentUrl)) {
      this.showOneTrust = false
    } else {
      this.showOneTrustClass = true
    }

    // Temporary hack to bring life to a Drömspelet page
    if (this.contentId === 'content_23296' || this.contentId === 'content_23297') {
      document.body.classList.add("dromspelet")
    } else {
      document.body.classList.remove("dromspelet")
    }
  },

}
</script>
<style lang="scss">
// Temporary hack to bring life to a Drömspelet page
body.dromspelet {
  background-image: url('https://assets.icanet.se/image/upload/q_40/v1655208422/test/grass-750.webp');

  .section-heading-name {
    color: white;
  }

  .on-page-breadcrumbs {
    a,
    span {
      color: white;
      text-shadow: 2px 2px 2px black;
      background-color: rgba(0, 0, 0, 0.3);
      box-shadow: 0 0 20px rgb(0 0 0);
    }

    a::after {
      border-color: white;
    }
  }
}

.info-page-wrapper {
  padding: 0;
}

.info-page {
  margin: 0 -1rem;
  padding: 2rem 0  3.5rem 0;
  border-radius: 0.25rem;

  @include media-breakpoint-up(md) {
    margin: 0;
    padding: 3rem 0  4.5rem 0;
  }

  @include media-breakpoint-up(lg) {
    padding: 3.5rem 0  5.5rem 0;
  }

  @include media-breakpoint-up(xl) {
    padding: 4rem 0  6.5rem 0;
  }

  background-color: $color-white;

  &__content {
    @include font(text,3, "normal");

    margin-bottom: 2.5rem;
  }

  .epi-background-container,
  .empty-image-placeholder {
    border-radius: 0.25rem;
    padding-bottom: 56.25%;
  }

  .empty-image-placeholder {
    height: auto;
  }

  &__form-wrapper {
    padding: 0 1.5rem;

    @include media-breakpoint-up(md) {
      padding: 0 6.4375rem;
    }
  }

  .offer {
    margin: 1rem 0.5rem;
    border: 0.0625rem solid $color-grey-special;

    @include media-breakpoint-up(md) {
      margin: 1.5rem 0;
    }

    &__content {
      text-align: left;
      min-height: 0;

      h3 {
        max-width: calc(100% - 2rem);

        @include media-breakpoint-up(md) {
          max-width: calc(100% - 8rem);
        }
      }

      .offer__rules {
        margin-bottom: 0;
      }
    }

    &__content-wrapper {
      padding: 1rem;
      flex-direction: row;
    }

    &__image-wrapper {
      margin-bottom: 0;

      @include media-breakpoint-up(md) {
        margin-right: 2rem;
        min-width: 10rem;
      }

      .epi-picture {
        text-align: center;

        img {
          max-height: 10rem;
        }
      }
    }

    .button--text-icon {
      margin: 0;
      width: auto;
    }
  }
}

.info-page-accordion {
  margin-top: 2rem;

  @include media-breakpoint-up(md) {
    margin-top: 3rem;
  }

  @include media-breakpoint-up(lg) {
    margin-top: 3.5rem;
  }

  @include media-breakpoint-up(xl) {
    margin-top: 4rem;
  }
}

</style>

